
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ReportHistory } from './ReportHistory';
import { UpcomingDeadlines } from './UpcomingDeadlines';
import { useReportDeadlines } from '@/hooks/useReportDeadlines';
import { ReportPDFGenerator } from './reports/ReportPDFGenerator';
import { FileText, Calendar } from 'lucide-react';
import { ResponsiveTable } from '@/components/ui/responsive-table';

export function QuarterlyReportsContent() {
  const [selectedState, setSelectedState] = useState<string>('FL');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState<number>(
    Math.floor((new Date().getMonth() / 3)) + 1
  );
  const [generatedReport, setGeneratedReport] = useState<any>(null);
  const [generatedDocumentInfo, setGeneratedDocumentInfo] = useState<{fileUrl: string, documentId: string} | null>(null);

  const { 
    generateReport, 
    markReportComplete, 
    isLoading 
  } = useReportDeadlines();

  const handleGenerateReport = async () => {
    const reportData = await generateReport({
      state: selectedState,
      year: selectedYear,
      quarter: selectedQuarter
    });
    
    setGeneratedReport(reportData);
  };

  const handleSubmitReport = async () => {
    if (generatedReport) {
      await markReportComplete({
        state: selectedState,
        year: selectedYear,
        quarter: selectedQuarter,
        reportData: generatedReport
      });
      
      // Reset state after submission
      setGeneratedReport(null);
      setGeneratedDocumentInfo(null);
    }
  };

  const handleReportGenerated = (result: {fileUrl: string, documentId: string}) => {
    setGeneratedDocumentInfo(result);
  };
  
  const handleSelectDeadline = (year: number, quarter: number, state: string) => {
    setSelectedYear(year);
    setSelectedQuarter(quarter);
    setSelectedState(state);
    // Scroll to or focus on the report generation section
    const tabsElement = document.querySelector('[data-value="generate"]');
    if (tabsElement) {
      (tabsElement as HTMLElement).click();
    }
  };
  
  const handleDownloadReport = (reportId: string, state: string, year: number, quarter: number) => {
    console.log(`Downloading report ${reportId} for ${state} Q${quarter} ${year}`);
    // Implementation would typically fetch the document URL and trigger download
  };
  
  const handleMarkAsSubmitted = (reportId: string) => {
    console.log(`Marking report ${reportId} as submitted`);
    // Implementation would update the report status in the database
  };

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <h1 className="text-xl md:text-2xl font-bold">Quarterly Reports</h1>
      </div>
      
      <Tabs defaultValue="generate" className="w-full">
        <TabsList className="w-full flex flex-wrap gap-1 md:gap-0 md:grid md:grid-cols-3">
          <TabsTrigger value="generate" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="whitespace-nowrap">Generate Reports</span>
          </TabsTrigger>
          <TabsTrigger value="deadlines" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span className="whitespace-nowrap">Upcoming Deadlines</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="whitespace-nowrap">Report History</TabsTrigger>
        </TabsList>
        
        <TabsContent value="generate" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Generate Quarterly Report</CardTitle>
              <CardDescription>
                Generate regulatory reports for submission to state authorities
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Select
                    value={selectedState}
                    onValueChange={setSelectedState}
                  >
                    <SelectTrigger id="state">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FL">Florida</SelectItem>
                      <SelectItem value="CA">California</SelectItem>
                      <SelectItem value="TX">Texas</SelectItem>
                      <SelectItem value="NY">New York</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="year">Year</Label>
                  <Select
                    value={selectedYear.toString()}
                    onValueChange={(value) => setSelectedYear(parseInt(value))}
                  >
                    <SelectTrigger id="year">
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="quarter">Quarter</Label>
                  <Select
                    value={selectedQuarter.toString()}
                    onValueChange={(value) => setSelectedQuarter(parseInt(value))}
                  >
                    <SelectTrigger id="quarter">
                      <SelectValue placeholder="Select quarter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Q1 (Jan-Mar)</SelectItem>
                      <SelectItem value="2">Q2 (Apr-Jun)</SelectItem>
                      <SelectItem value="3">Q3 (Jul-Sep)</SelectItem>
                      <SelectItem value="4">Q4 (Oct-Dec)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button 
                onClick={handleGenerateReport} 
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? 'Generating...' : 'Generate Report Data'}
              </Button>
            </CardContent>
          </Card>
          
          {generatedReport && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Report Preview</CardTitle>
                  <CardDescription>
                    Report for {selectedState} - Q{selectedQuarter} {selectedYear}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-md bg-muted p-4">
                    <p><strong>State:</strong> {selectedState}</p>
                    <p><strong>Reporting Period:</strong> Q{selectedQuarter} {selectedYear}</p>
                    <p><strong>Certificates:</strong> {generatedReport.certificatesCount || 0}</p>
                    <p><strong>Generated:</strong> {new Date().toLocaleString()}</p>
                  </div>
                  
                  <Button 
                    onClick={handleSubmitReport} 
                    disabled={isLoading || !!generatedDocumentInfo}
                  >
                    {isLoading ? 'Submitting...' : 'Mark as Submitted'}
                  </Button>
                </CardContent>
              </Card>
              
              <ReportPDFGenerator
                state={selectedState}
                year={selectedYear}
                quarter={selectedQuarter}
                reportData={generatedReport}
                onReportGenerated={handleReportGenerated}
              />
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="deadlines">
          <UpcomingDeadlines onSelectDeadline={handleSelectDeadline} />
        </TabsContent>
        
        <TabsContent value="history">
          <ResponsiveTable>
            <ReportHistory 
              onDownload={handleDownloadReport} 
              onMarkAsSubmitted={handleMarkAsSubmitted} 
            />
          </ResponsiveTable>
        </TabsContent>
      </Tabs>
    </div>
  );
}
